// frontend/src/hooks/useUsers.ts
// React Query hooks for user management service operations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  userService,
  User,
  UserFilters,
  UserFormData
} from '@/lib/api/userService';
import { tokenManager } from '@/lib/httpClient';

// Query keys for users
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: UserFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: number) => [...userKeys.details(), id] as const,
  stats: () => [...userKeys.all, 'stats'] as const,
  activity: () => [...userKeys.all, 'activity'] as const,
  roles: () => [...userKeys.all, 'roles'] as const,
  permissions: () => [...userKeys.all, 'permissions'] as const,
} as const;

// Get users with filters (Admin only)
export const useUsers = (filters: UserFilters = {}) => {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: async () => {
      const response = await userService.getUsers(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get users with integrated stats (Admin only) - Use this instead of separate calls
export const useUsersWithStats = (filters: UserFilters = {}) => {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: async () => {
      const response = await userService.getUsers(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
    select: (data) => {
      // Transform the data to separate users and stats
      return {
        users: data.users || data.data || [],
        pagination: data.pagination || null,
        stats: data.stats || null
      };
    }
  });
};

// Get specific user by ID (Admin only)
export const useUser = (id: number) => {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: async () => {
      const response = await userService.getUserById(id);
      if (response.success && response.data) {
        return response.data.user;
      }
      throw new Error(response.message);
    },
    enabled: !!id && !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Create new user (Admin only)
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UserFormData) => {
      const response = await userService.createUser(data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (newUser) => {
      // Invalidate users lists
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
      
      // Add new user to cache if we have the data
      if (newUser?.user) {
        queryClient.setQueryData(
          userKeys.detail(newUser.user.user_id),
          newUser.user
        );
      }
    },
  });
};

// Update user (Admin only)
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<UserFormData> }) => {
      const response = await userService.updateUser(id, data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedUser, variables) => {
      // Update specific user in cache
      if (updatedUser?.user) {
        queryClient.setQueryData(
          userKeys.detail(variables.id),
          updatedUser.user
        );
      }
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
    },
  });
};

// Delete user (Admin only)
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await userService.deleteUser(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: userKeys.detail(variables) });
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
    },
  });
};

// Toggle user status (Admin only)
export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await userService.toggleUserStatus(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedUser, variables) => {
      // Update specific user in cache
      if (updatedUser?.user) {
        queryClient.setQueryData(
          userKeys.detail(variables),
          updatedUser.user
        );
      }
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

// Update user role (Admin only)
export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, role }: { id: number; role: string }) => {
      const response = await userService.updateUserRole(id, role);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedUser, variables) => {
      // Update specific user in cache
      if (updatedUser?.user) {
        queryClient.setQueryData(
          userKeys.detail(variables.id),
          updatedUser.user
        );
      }
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
    },
  });
};

// Reset user password (Admin only)
export const useResetUserPassword = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await userService.resetUserPassword(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};


// Get user statistics (Admin only)
export const useUserStats = () => {
  return useQuery({
    queryKey: userKeys.stats(),
    queryFn: async () => {
      const response = await userService.getUserStats();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get user activity logs (Admin only)
export const useUserActivity = (userId?: number, filters: any = {}) => {
  return useQuery({
    queryKey: [...userKeys.activity(), userId, filters],
    queryFn: async () => {
      const response = await userService.getUserActivity(userId, filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Search users
export const useSearchUsers = (searchQuery: string, filters: UserFilters = {}) => {
  return useQuery({
    queryKey: [...userKeys.lists(), 'search', searchQuery, filters],
    queryFn: async () => {
      const response = await userService.getUsers({ 
        ...filters, 
        search: searchQuery 
      });
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!searchQuery && searchQuery.length > 2 && !!tokenManager.getToken(),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get users by role
export const useUsersByRole = (role: string) => {
  return useQuery({
    queryKey: [...userKeys.roles(), role],
    queryFn: async () => {
      const response = await userService.getUsers({ role });
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!role && !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get active providers
export const useActiveProviders = () => {
  return useQuery({
    queryKey: [...userKeys.roles(), 'providers', 'active'],
    queryFn: async () => {
      const response = await userService.getActiveProviders();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Verify user email (Admin action)
export const useVerifyUserEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await userService.verifyUserEmail(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedUser, variables) => {
      // Update specific user in cache
      if (updatedUser?.user) {
        queryClient.setQueryData(
          userKeys.detail(variables),
          updatedUser.user
        );
      }
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

// Unverify user email (Admin action)
export const useUnverifyUserEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await userService.unverifyUserEmail(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedUser, variables) => {
      // Update specific user in cache
      if (updatedUser?.user) {
        queryClient.setQueryData(
          userKeys.detail(variables),
          updatedUser.user
        );
      }
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

// Send notification to user (Admin action)
export const useSendUserNotification = () => {
  return useMutation({
    mutationFn: async ({ 
      userId, 
      title, 
      message, 
      type 
    }: { 
      userId: number; 
      title: string; 
      message: string; 
      type?: string; 
    }) => {
      const response = await userService.sendUserNotification(userId, title, message, type);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// Impersonate user (Super Admin only)
export const useImpersonateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await userService.impersonateUser(userId);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (impersonationData) => {
      // Store impersonation token
      if (impersonationData?.token) {
        tokenManager.setToken(impersonationData.token);
      }
      
      // Update user data in cache
      if (impersonationData?.user) {
        queryClient.setQueryData(['auth', 'user'], impersonationData.user);
      }
      
      // Clear other cached data to prevent conflicts
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};

// Stop impersonation (Super Admin only)
export const useStopImpersonation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await userService.stopImpersonation();
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (originalData) => {
      // Restore original token
      if (originalData?.token) {
        tokenManager.setToken(originalData.token);
      }
      
      // Update user data back to original
      if (originalData?.user) {
        queryClient.setQueryData(['auth', 'user'], originalData.user);
      }
      
      // Refresh all data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};

